import axios from 'axios'
import { useState, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { useTranslation, TFunction } from 'react-i18next'
import { setProfile } from '../../redux/action/profile'
import useLoadingStore from '../../stores/useLoadingStore'
import useRelogin from '../useRelogin'
import ConfigAPI from '../../constant/ConfigAPI'
import { RootStateType } from '../../redux/reducer/rootReducer'

type ParamServerSideType = {
    pageNumber: number
    rowPerPage: number
    sortField: string
    sortType: string
    keyword: string
}

export type UserPermissionType = {
    id: number
    name: string
    checked: boolean
}

export interface Form {
    userGroupName: string
    description?: string
    userPermission?: any[]
}

export const validate = async (values: Form, t: TFunction<'translation'>) => {
    const errors: Partial<Form> = {}
    if (!values.userGroupName) {
        errors.userGroupName = `${t('LABEL.TEXT_REQUIRED')}`
    }
    return errors
}

const useUserGroup = () => {
    const valueSubmitted = useRef<Form>()
    const [permissionList, setPermissionList] = useState<UserPermissionType[]>([])
    const [openDialogOkDelete, setOpenDialogOkDelete] = useState<boolean>(false)
    const [openDialogDelete, setOpenDialogDelete] = useState<boolean>(false)
    const [openDialogBack, setOpenDialogBack] = useState<boolean>(false)
    const [openDialogSave, setOpenDialogSave] = useState<boolean>(false)
    const [openDialogOk, setOpenDialogOk] = useState<boolean>(false)
    const [alertMessageOk, setAlertMessageOk] = useState<string>('')
    const [openDialogError, setOpenDialogError] = useState<boolean>(false)
    const [alertMessageError, setAlertMessageError] = useState<string>('')
    const [statusCode, setStatusCode] = useState<number>()
    const [isDuplicate, setIsDuplicate] = useState<boolean>(false)
    const [actionType, setActionType] = useState<string>('')
    const [data, setData] = useState<any[]>([])
    const [totalItem, setTotalItem] = useState<number>(0)
    const [userGroupId, setUserGroupId] = useState<number>(0)
    const [paramServerSide, setParamServerSide] = useState<ParamServerSideType>({
        pageNumber: 1,
        rowPerPage: 10,
        sortField: '',
        sortType: '',
        keyword: '',
    })
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const history = useHistory()
    const loadingStore = useLoadingStore()
    const { isLoggedIn } = useSelector(({ profile }: RootStateType) => profile)

    useEffect(() => {
        if (isLoggedIn) {
            callServiceSearch(paramServerSide)
            callServiceDetailSetup()
        }
    }, [isLoggedIn])

    const callServiceSearch = async (params: ParamServerSideType) => {
        try {
            loadingStore.setLoading(true)
            const url = `${process.env.REACT_APP_API}/api/users/group/?pageNumber=${params.pageNumber}&rowPerPage=${params.rowPerPage}&sortField=${params.sortField}&sortType=${params.sortType}&keyword=${params.keyword}`
            const result = await axios.get(url, ConfigAPI)
            if (result) {
                setData(result.data.data)
                setTotalItem(result.data.total)
                setParamServerSide({
                    pageNumber: params.pageNumber,
                    rowPerPage: params.rowPerPage,
                    sortField: params.sortField,
                    sortType: params.sortType,
                    keyword: params.keyword,
                })
            }
        } catch (error) {
            //@ts-ignore
            const errMsg: string = error?.toString()
            if (errMsg) {
                if (errMsg.includes('401')) {
                    //reLoginForTokenExpired('/backoffice/usergroup-management')
                    //callServiceReLogin('/backoffice/usergroup-management')
                }
            }
            return { error }
        } finally {
            loadingStore.setLoading(false)
        }
    }

    const callServiceDetailSetup = async () => {
        try {
            loadingStore.setLoading(true)
            const url = `${process.env.REACT_APP_API}/api/users/group/detailsetup`
            const result = await axios.get(url, ConfigAPI)

            if (result.data.permissionList) {
                const data = result.data.permissionList.map((item: LookupType) => ({
                    id: item.id,
                    name: item.name,
                    checked: false,
                }))
                setPermissionList(data)
            }

            return { result }
        } catch (error) {
            return { error }
        } finally {
            loadingStore.setLoading(false)
        }
    }

    const callServiceGetById = async (id: number) => {
        try {
            loadingStore.setLoading(true)
            const url = `${process.env.REACT_APP_API}/api/users/group/${id}`
            const result = await axios.get(url, ConfigAPI)
            if (result.data) {
                setStatusCode(200)
            }
            return { result }
        } catch (error) {
            //@ts-ignore
            const errMsg: string = error?.toString()
            if (errMsg) {
                if (errMsg.includes('401')) {
                    //reLoginForTokenExpired()
                    //callServiceReLogin()
                } else {
                    setStatusCode(500)
                    setOpenDialogError(true)
                    setAlertMessageError(t('ALERT.CONTENT.NOT_RETRIEVE_DATA'))
                }
            }
            return { error }
        } finally {
            loadingStore.setLoading(false)
        }
    }

    const callServiceAdd = async (data: Form) => {
        try {
            loadingStore.setLoading(true)
            const url = `${process.env.REACT_APP_API}/api/users/group`
            const reqParams = {
                name: data.userGroupName,
                description: data.description,
                permission: data.userPermission
                    ?.map((item, index) => {
                        if (item) {
                            return index + 1
                        }
                    })
                    .filter((item) => item)
                    ?.join(','),
            }
            const result = await axios.post(url, reqParams, ConfigAPI)
            if (result.data.message === 'success') {
                setStatusCode(200)
                setAlertMessageOk(t('ALERT.CONTENT.SAVE_SUCCESS'))
            } else if (result.data.message === 'duplicated') {
                setStatusCode(200)
                setIsDuplicate(true)
                setAlertMessageOk(t('ALERT.CONTENT.SAVE_DUPLICATED'))
            } else {
                setStatusCode(200)
                setAlertMessageOk(result.data.message)
            }
            setOpenDialogOk(true)

            return { result }
        } catch (error) {
            //@ts-ignore
            const errMsg: string = error?.toString()
            if (errMsg) {
                if (errMsg.includes('401')) {
                    //reLoginForTokenExpired()
                    //callServiceReLogin()
                } else {
                    setStatusCode(500)
                    setOpenDialogError(true)
                    setAlertMessageError(t('ALERT.CONTENT.SAVE_NOT_SUCCESS'))
                }
            }
            return { error }
        } finally {
            loadingStore.setLoading(false)
        }
    }

    const callServiceEdit = async (id: number, data: Form) => {
        try {
            loadingStore.setLoading(true)
            const url = `${process.env.REACT_APP_API}/api/users/group/${id}`
            const reqParams = {
                name: data.userGroupName,
                description: data.description,
                permission: data.userPermission
                    ?.map((item, index) => {
                        if (item) {
                            return index + 1
                        }
                    })
                    .filter((item) => item)
                    ?.join(','),
            }
            const result = await axios.put(url, reqParams, ConfigAPI)
            if (result.data.message === 'success') {
                setStatusCode(200)
                setAlertMessageOk(t('ALERT.CONTENT.SAVE_SUCCESS'))
                callServiceFetchProfile()
            } else if (result.data.message === 'duplicated') {
                setStatusCode(200)
                setIsDuplicate(true)
                setAlertMessageOk(t('ALERT.CONTENT.SAVE_DUPLICATED'))
            } else {
                setStatusCode(200)
                setAlertMessageOk(result.data.message)
            }
            setOpenDialogOk(true)

            return { result }
        } catch (error) {
            //@ts-ignore
            const errMsg: string = error?.toString()
            if (errMsg) {
                if (errMsg.includes('401')) {
                    //reLoginForTokenExpired()
                    //callServiceReLogin()
                } else {
                    setStatusCode(500)
                    setOpenDialogError(true)
                    setAlertMessageError(t('ALERT.CONTENT.SAVE_NOT_SUCCESS'))
                }
            }
            return { error }
        } finally {
            loadingStore.setLoading(false)
        }
    }

    const callServiceDelete = async (id: number) => {
        try {
            loadingStore.setLoading(true)
            const url = `${process.env.REACT_APP_API}/api/users/group/${id}`
            const result = await axios.delete(url, ConfigAPI)
            if (result.data.message === 'success') {
                setData(data.filter((item) => item.groupId !== userGroupId))
                setTotalItem(totalItem - 1)
                setUserGroupId(0)
            }
            setOpenDialogOkDelete(true)

            return { result }
        } catch (error) {
            return { error }
        } finally {
            loadingStore.setLoading(false)
        }
    }

    const callServiceFetchProfile = async () => {
        try {
            loadingStore.setLoading(true)
            const url = `${process.env.REACT_APP_API}/api/auth/fetchProfile`
            const result = await axios.get(url, ConfigAPI)
            if (result.status === 200) {
                dispatch(setProfile(result.data))
            }
            return { result }
        } catch (error) {
            return { error }
        } finally {
            loadingStore.setLoading(false)
        }
    }

    const handleChangePageNumber = (page: number) => {
        callServiceSearch({
            ...paramServerSide,
            pageNumber: page,
        })
    }

    const handleSort = (sortField: string, sortType: string) => {
        const params: ParamServerSideType = {
            ...paramServerSide,
            sortField: sortField,
            sortType: sortType,
        }
        callServiceSearch(params)
    }

    const handleSubmitSearch = (values: { keyword: string }) => {
        const params: ParamServerSideType = {
            ...paramServerSide,
            pageNumber: 1,
            keyword: values?.keyword ? values.keyword.trim() : '',
        }
        callServiceSearch(params)
    }

    const handleSubmitForm = (values: any) => {
        valueSubmitted.current = values
        setActionType('save')
        setOpenDialogSave(true)
    }

    const handleClickAdd = () => {
        history.push('/backoffice/usergroup-management/add')
    }

    const handleClickEdit = async (data: any) => {
        const groupId = data.groupId
        setUserGroupId(groupId)
        history.push(`/backoffice/usergroup-management/${groupId}/edit`)
    }

    const handleClickDelete = (data: any) => {
        const groupId = data.groupId
        setUserGroupId(groupId)
        setOpenDialogDelete(true)
    }

    const handleClickForceBack = () => {
        history.push('/backoffice/usergroup-management')
    }

    const handleClickBack = (dirty: boolean) => {
        // ตอนกดปุ่มยกเลิกที่ form
        if (dirty) {
            setOpenDialogBack(true)
        } else {
            setOpenDialogBack(false)
            history.push('/backoffice/usergroup-management')
        }
        setActionType('back')
    }

    const handleClickCancelDelete = () => {
        setOpenDialogDelete(false)
    }

    const handleClickConfirmDelete = () => {
        setOpenDialogDelete(false)
        callServiceDelete(userGroupId)
    }

    const handleClickCancelBack = () => {
        setOpenDialogBack(false)
    }

    const handleClickConfirmBack = () => {
        setOpenDialogBack(false)
        history.push('/backoffice/usergroup-management')
    }

    const handleClickCancelSave = () => {
        setOpenDialogSave(false)
    }

    const handleClickConfirmSave = (type: 'add' | 'edit', id: number) => {
        if (type === 'add') {
            callServiceAdd(valueSubmitted.current as Form)
        }
        if (type === 'edit') {
            callServiceEdit(id, valueSubmitted.current as Form)
        }

        setOpenDialogSave(false)
    }

    const handleClickOk = () => {
        setOpenDialogOk(false)
        setOpenDialogError(false)
        if (statusCode === 401) {
            history.push('/login')
        } else if (!isDuplicate && statusCode !== 500) {
            history.push('/backoffice/usergroup-management')
        }
        setIsDuplicate(false)
    }

    const handleClickOkDelete = () => {
        setOpenDialogOkDelete(false)
    }

    return {
        onSubmitForm: handleSubmitForm,
        onSubmitSearch: handleSubmitSearch,
        onChangePageNumber: handleChangePageNumber,
        onSort: handleSort,
        onClickAdd: handleClickAdd,
        onClickEdit: handleClickEdit,
        onClickDelete: handleClickDelete,
        onClickOkDelete: handleClickOkDelete,
        onClickCancelBack: handleClickCancelBack,
        onClickCancelSave: handleClickCancelSave,
        onClickCancelDelete: handleClickCancelDelete,
        onClickConfirmBack: handleClickConfirmBack,
        onClickConfirmSave: handleClickConfirmSave,
        onClickConfirmDelete: handleClickConfirmDelete,
        onClickForceBack: handleClickForceBack,
        onClickBack: handleClickBack,
        onClickOk: handleClickOk,
        callServiceGetById,
        permissionList,
        userGroupId,
        data,
        totalItem,
        paramServerSide,
        openDialogOkDelete,
        openDialogDelete,
        openDialogBack,
        openDialogSave,
        openDialogOk,
        alertMessageOk,
        openDialogError,
        alertMessageError,
        actionType,
    }
}

export default useUserGroup
